import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { getHeaderLayout, getLeftHeaderLayout } from "./loader";
import { HeaderSelectors, UserContextSelectors } from "../../redux/selectors";
import {
  useHeaderHeightResizeDetector,
  useHeaderWidthResizeDetector
} from "./hooks";
import { useSelector } from "react-redux";
import {
  DeviceType,
  IgnoreClickOutsideAttribute,
  CSSVarNames
} from "../../constants";
import Search from "../../components/Menu/Search/SearchHeaderSection";
import ActionIndicatorContainer from "../../components/ActionIndicatorContainer/ActionIndicatorContainer";
import LastLogin from "../../components/LastLogin";
import styled from "styled-components";
import AdditionalHeaderText from "./AdditionalHeaderText";
import { useFeature } from "../../hooks/useFeature";
import { AppFeatures } from "../../constants/featureFlags";
import { Brand } from "../../configurations";
import { BrandUtils } from "../../utils/brandUtils";
import classNames from "classnames";
import { ActionIndicatorWrapper } from "./HeaderWrapper";
import HeaderRegulatoryText from "./HeaderRegulatoryText";

const CollapseCrossActivityClass = "collapse-cross-activity";

const SegmentedHeaderWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: stretch;
`;

const Header = () => {
  const brandId = useSelector(UserContextSelectors.brandId);
  const isPC = useSelector(UserContextSelectors.isPC);
  const searchEnabled = BrandUtils.isSearchEnable();
  const isAdditionalTextEnabled = useFeature(
    AppFeatures.ADDITIONAL_HEADER_TEXT
  );

  const isCollapsedCrossActivity = useSelector(
    HeaderSelectors.isCollapsedCrossActivity
  );

  const [hasActionIndicator, setHasActionIndicator] = useState(false);
  const [hasHeaderRegulatoryText, setHeaderRegulatoryText] = useState(false);

  useEffect(() => {
    const actionIndicatorFlag =
      brandId === Brand.Sport_Com || BrandUtils.isPokerClient();
    setHasActionIndicator(actionIndicatorFlag);
  }, [brandId]);

  useEffect(() => {
    const headerRegulatotyTextFlag = brandId === Brand.Sport_IT;
    setHeaderRegulatoryText(headerRegulatotyTextFlag);
  }, [brandId]);

  const SegmentedHeaderPC = useMemo(
    () => getHeaderLayout(brandId, DeviceType.PC),
    [brandId]
  );
  const SegmentedHeaderMobile = useMemo(
    () => getHeaderLayout(brandId, DeviceType.Mobile),
    [brandId]
  );
  const SegmentedLeftHeader = useMemo(
    () => getLeftHeaderLayout(brandId, DeviceType.PC),
    [brandId]
  );

  // Kambi based instances (only Italy left) flush the css var set initially on the page
  // This is a patch to support that, once Kambi is removed this should too
  useEffect(() => {
    if (brandId !== Brand.Sport_IT) return;
    const handleLoad = () => {
      const header = document.querySelector(".headerWrapper");
      const headerHeight = header?.getBoundingClientRect().height || 61;

      document.body.style.setProperty(
        CSSVarNames.HeaderPC,
        `${headerHeight}px`
      );
    };

    window.addEventListener("load", handleLoad);
    return () => window.removeEventListener("load", handleLoad);
  }, [brandId]);

  const headerRefHeight = useHeaderHeightResizeDetector();
  const headerRefWidth = useHeaderWidthResizeDetector();

  const customAttributes = useMemo(() => {
    return !isPC ? { [IgnoreClickOutsideAttribute]: "" } : {};
  }, [isPC]);

  const classes = useMemo(
    () =>
      classNames("uc-main-header", {
        [CollapseCrossActivityClass]: isCollapsedCrossActivity
      }),
    [isCollapsedCrossActivity]
  );

  return (
    <header className={classes} {...customAttributes}>
      <div
        className="headerWrapper"
        ref={el => {
          headerRefHeight.current = el;
          headerRefWidth.current = el;
        }}
      >
        {isPC ? (
          <div className="header-left-sect-pc">
            <SegmentedLeftHeader />
            {searchEnabled && <Search ignoreClass="do-not-close-search" />}
          </div>
        ) : (
          <>
            <LastLogin />
            {isAdditionalTextEnabled && <AdditionalHeaderText />}
          </>
        )}
        {hasHeaderRegulatoryText && <HeaderRegulatoryText />}
        <SegmentedHeaderWrapper>
          {hasActionIndicator && (
            <ActionIndicatorWrapper>
              <ActionIndicatorContainer />
            </ActionIndicatorWrapper>
          )}
          {isPC ? (
            <SegmentedHeaderPC key="SegmentedHeaderPC" />
          ) : (
            <SegmentedHeaderMobile key="SegmentedHeaderMobile" />
          )}
        </SegmentedHeaderWrapper>
      </div>
    </header>
  );
};

Header.propTypes = {
  actionIndicator: PropTypes.node
};

export default React.memo(Header);
